import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Typography, Button, useTheme, useMediaQuery, LinearProgress } from '@material-ui/core';
import Page from 'src/components/Page';
import PermissionProvider from 'src/utils/PermissionProvider';
import { isAdmin } from 'src/utils';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		paddingTop: '10vh',
		display: 'flex',
		flexDirection: 'column',
		alignContent: 'center'
	},
	buttonContainer: {
		marginTop: theme.spacing(6),
		display: 'flex',
		justifyContent: 'center'
	}
}));

function Forbidden() {
	const classes = useStyles();
	const theme = useTheme();
	const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));

	return (
		<Page
			className={classes.root}
			title="Error 403"
		>
			<Typography
				align="center"
				variant={mobileDevice ? 'h4' : 'h1'}
			>
				403: You do not have permission to view this page.
			</Typography>
			<Typography
				align="center"
				variant="subtitle2"
			>
				If you believe you should have access, please contact your manager.
			</Typography>
			<div className={classes.buttonContainer}>
				<Button
					color="primary"
					component={RouterLink}
					to="/admin/dashboard"
					variant="outlined"
				>
					Back to dashboard
				</Button>
			</div>
		</Page>
	);
}

// Same matching rules as src/customComponents/ProtectedComponent.js, which gates the nav items.
export function hasPermission(userPermissions, permission) {
	if (permission === 'all') {
		return true;
	}

	if (permission === 'Admin' && isAdmin()) {
		return true;
	}

	if (permission instanceof Array) {
		return userPermissions.some((el) => permission.some((item) => el.value === item));
	}

	return userPermissions.some((el) => el.value === permission);
}

function RouteGuard({ component: Component, permission, ...rest }) {
	const userPermissions = useContext(PermissionProvider);

	// Permissions are fetched asynchronously in App.js and are null until they land.
	// Rendering Forbidden here would flash a 403 at every user on a hard page load.
	if (!userPermissions) {
		return <LinearProgress />;
	}

	return hasPermission(userPermissions, permission) ? <Component {...rest} /> : <Forbidden />;
}

RouteGuard.propTypes = {
	component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	permission: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};

const guardedComponent = (Component, permission) =>
	function GuardedRoute(props) {
		return (
			<RouteGuard
				{...props}
				component={Component}
				permission={permission}
			/>
		);
	};

/**
 * Wraps every route declaring a `permission` so a direct URL hit is checked, not just the nav
 * link. Recurses into `routes` so nested definitions rendered by a layout's own
 * `renderRoutes(route.routes)` are covered too. Call once at module scope.
 */
export function withRouteGuards(routes) {
	return routes.map((route) => {
		const guarded = { ...route };

		if (route.routes) {
			guarded.routes = withRouteGuards(route.routes);
		}

		if (route.permission) {
			guarded.component = guardedComponent(route.component, route.permission);
		}

		return guarded;
	});
}

export default RouteGuard;
